export interface AddressData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
}

export interface PersonalInformationData {
  givenName: string;
  dateOfBirth: string;
  nationality: string;
  gender: string;
  passportNumber?: string;
  passportExpiryDate?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  errors?: string[];
}

export function validateAddress(address: AddressData, addressType: 'residential' | 'mailing'): ValidationResult {
  const addressLabel = addressType === 'residential' ? 'Residential address' : 'Mailing address';

  // Validate required fields: country, addressLine1, city, province
  if (!address.country || address.country.trim() === '') {
    return {
      isValid: false,
      errorMessage: `${addressLabel} country is required`,
    };
  }

  if (!address.addressLine1 || address.addressLine1.trim() === '') {
    return {
      isValid: false,
      errorMessage: `${addressLabel} address line 1 is required`,
    };
  }

  if (!address.city || address.city.trim() === '') {
    return {
      isValid: false,
      errorMessage: `${addressLabel} city is required`,
    };
  }

  if (!address.province || address.province.trim() === '') {
    return {
      isValid: false,
      errorMessage: `${addressLabel} province/state/area is required`,
    };
  }

  // All validations passed
  return {
    isValid: true,
  };
}

export function validatePersonalInformation(
  data: PersonalInformationData,
  options: { requirePassport: boolean } = { requirePassport: false }
): ValidationResult {
  const errors: string[] = [];

  // Required field validation
  if (!data.givenName || data.givenName.trim() === '') {
    errors.push('Given name is required');
  }

  if (!data.dateOfBirth || data.dateOfBirth.trim() === '') {
    errors.push('Date of birth is required');
  }

  if (!data.nationality || data.nationality.trim() === '') {
    errors.push('Nationality is required');
  }

  if (!data.gender || data.gender.trim() === '') {
    errors.push('Gender is required');
  }

  // Conditional passport validation
  if (options.requirePassport) {
    if (!data.passportNumber || data.passportNumber.trim() === '') {
      errors.push('Passport number is required');
    }
    if (!data.passportExpiryDate || data.passportExpiryDate.trim() === '') {
      errors.push('Passport expiry date is required');
    }
  }

  // Business rule: Date of birth cannot be in the future
  if (data.dateOfBirth) {
    const dob = new Date(data.dateOfBirth);
    const today = new Date();
    if (dob > today) {
      errors.push('Date of birth cannot be in the future');
    }
  }

  // Return validation result
  if (errors.length > 0) {
    return {
      isValid: false,
      errors,
    };
  }

  return {
    isValid: true,
  };
}
